/**
 * roamerScoring.ts — the city-level Roamer Score™ engine.
 *
 * Takes raw 0–100 metrics per category (from a CityMetricsProvider) and the
 * user's profile, and produces a weighted, personalised, fully-broken-down
 * 0–100 score. Weights live in weights.ts and can be overridden per call —
 * nothing here is hardcoded to a city.
 *
 * Pure functions only — deterministic, no I/O, no React.
 */

import {
  ROAMER_SCORE_CATEGORIES,
  ROAMER_SCORE_CATEGORY_LABEL,
  type CityMetrics,
  type RoamerScore,
  type RoamerScoreBreakdownItem,
  type RoamerScoreCategory,
  type RoamerScoreTier,
} from '../types';
import type { RoamerProfile } from '../RoamerProfile';
import { DEFAULT_ROAMER_SCORE_WEIGHTS, personalizeRoamerScoreWeights } from './weights';

const clampScore = (x: number) => Math.max(0, Math.min(100, x));

function tierOf(overall: number): { tier: RoamerScoreTier; headline: string } {
  if (overall >= 92) return { tier: 'exceptional', headline: 'Exceptional Base' };
  if (overall >= 84) return { tier: 'excellent', headline: 'Excellent Base' };
  if (overall >= 74) return { tier: 'great', headline: 'Great Base' };
  if (overall >= 62) return { tier: 'good', headline: 'Good Base' };
  if (overall >= 50) return { tier: 'fair', headline: 'Workable Base' };
  return { tier: 'challenging', headline: 'Challenging Base' };
}

/**
 * Calculate the Roamer Score™ for a city.
 *
 * @param metrics          Raw 0–100 metric per category.
 * @param profile          Used to personalise weights (safety priority, budget, …).
 * @param weightOverrides  Optional per-category overrides applied BEFORE
 *                         personalisation — the "easily adjusted later" hook.
 */
export function calculateRoamerScore(
  metrics: CityMetrics,
  profile: RoamerProfile,
  weightOverrides?: Partial<Record<RoamerScoreCategory, number>>,
): RoamerScore {
  const base = { ...DEFAULT_ROAMER_SCORE_WEIGHTS, ...(weightOverrides ?? {}) };
  const weights = personalizeRoamerScoreWeights(base, profile);

  const totalWeight = ROAMER_SCORE_CATEGORIES.reduce((s, c) => s + weights[c], 0);

  const breakdown: RoamerScoreBreakdownItem[] = ROAMER_SCORE_CATEGORIES.map((category) => {
    const weight = totalWeight > 0 ? weights[category] / totalWeight : 0;
    const score = clampScore(metrics[category] ?? 50);
    return {
      category,
      label: ROAMER_SCORE_CATEGORY_LABEL[category],
      score,
      weight,
      contribution: score * weight,
    };
  });

  const overall = Math.round(breakdown.reduce((s, b) => s + b.contribution, 0));
  const { tier, headline } = tierOf(overall);

  return { overall: clampScore(overall), breakdown, headline, tier };
}
